import { useEffect, useState, useRef, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Plus, Edit3, Trash2, Download, X, Star, Sparkles, PackageX, ChevronLeft, ChevronRight, BarChart3, Package, Users, Receipt, Mail, Send, CreditCard, Radar } from "lucide-react";
import PaymentsPanel from "@/pages/admin/PaymentsPanel";
import AnalyticsPanel from "@/pages/admin/AnalyticsPanel";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import InvoiceDocsPanel from "@/pages/admin/InvoiceDocsPanel";
import LibraryInvoicePanel from "@/pages/admin/LibraryInvoicePanel";

const emptyForm = {
  title: "", author: "", publisher: "", category: "", subject: "",
  class_level: "", isbn: "", price: 0, mrp: 0,
  price_usd_override: null, mrp_usd_override: null,
  price_gbp_override: null, mrp_gbp_override: null,
  price_eur_override: null, mrp_eur_override: null,
  stock: 0, description: "", image_url: "",
  content: "", book_size: "",
  is_new_arrival: false, is_featured: false, out_of_stock: false,
  slug: "", meta_title: "", meta_description: "",
};

const PAGE_SIZE = 25;

export default function Admin() {
  const [tab, setTab] = useState("dashboard");
  const [books, setBooks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(0);
  const ORDERS_PAGE_SIZE = 50;
  const [orderDateFrom, setOrderDateFrom] = useState("");
  const [orderDateTo, setOrderDateTo] = useState("");
  const [orderSearchQ, setOrderSearchQ] = useState("");
  const [revenueRows, setRevenueRows] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [cancelReasons, setCancelReasons] = useState(null);
  // Cancellation modal state
  const [cancelModal, setCancelModal] = useState(null);  // { orderId, orderNumber } | null
  const [cancelReasonChoice, setCancelReasonChoice] = useState("Out of stock");
  const [cancelReasonOther, setCancelReasonOther] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  // Email invoice modal state
  const [invoiceModal, setInvoiceModal] = useState(null);  // { order, pdfUrl } | null
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [q, setQ] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterFlag, setFilterFlag] = useState("");
  const [categories, setCategories] = useState([]);
  const fileRef = useRef(null);

  const loadBooks = useCallback(async () => {
    try {
      const params = { q: q || undefined, limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (filterCategory) params.category = filterCategory;
      if (filterFlag === "new_arrival") params.new_arrival = true;
      if (filterFlag === "featured") params.featured = true;
      if (filterFlag === "in_stock") params.in_stock_only = true;
      if (filterFlag === "out_of_stock") params.out_of_stock_only = true;
      const { data } = await api.get("/books", { params });
      setBooks(data.items);
      setTotal(data.total);
    } catch (e) { toast.error(apiError(e)); }
  }, [q, page, filterCategory, filterFlag]);

  const loadOrders = useCallback(async () => {
    try {
      const params = { limit: ORDERS_PAGE_SIZE, skip: ordersPage * ORDERS_PAGE_SIZE };
      if (orderDateFrom) params.date_from = orderDateFrom;
      if (orderDateTo) params.date_to = orderDateTo;
      if (orderSearchQ) params.q = orderSearchQ;
      const { data } = await api.get("/admin/orders", { params });
      // New paginated shape: {items,total,limit,skip}. Fallback if server still returns array.
      if (Array.isArray(data)) {
        setOrders(data);
        setOrdersTotal(data.length);
      } else {
        setOrders(data.items || []);
        setOrdersTotal(data.total || 0);
      }
    } catch (e) { toast.error(apiError(e)); }
  }, [ordersPage, orderDateFrom, orderDateTo, orderSearchQ]);

  const loadRevenue = async () => {
    try {
      const params = {};
      if (orderDateFrom) params.date_from = orderDateFrom;
      if (orderDateTo) params.date_to = orderDateTo;
      const { data } = await api.get("/admin/revenue-by-day", { params });
      setRevenueRows(data.rows || []);
    } catch { /* silent */ }
  };

  const downloadPdf = async (url, filename) => {
    try {
      const { data } = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openInvoiceEmail = async (order) => {
    if (!order.user_email) { toast.error("This order has no customer email"); return; }
    try {
      const { data } = await api.get(`/admin/orders/${order.id}/invoice.pdf`, { responseType: "blob" });
      const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      setInvoiceModal({ order, pdfUrl });
    } catch (e) { toast.error(apiError(e)); }
  };

  const closeInvoiceModal = () => {
    if (invoiceModal?.pdfUrl) URL.revokeObjectURL(invoiceModal.pdfUrl);
    setInvoiceModal(null);
  };

  const sendInvoiceEmail = async () => {
    if (!invoiceModal?.order?.id) return;
    setInvoiceSending(true);
    try {
      const { data } = await api.post(`/admin/orders/${invoiceModal.order.id}/email-invoice`);
      toast.success(`Invoice emailed to ${data.to}`);
      closeInvoiceModal();
      loadOrders();
    } catch (e) { toast.error(apiError(e)); }
    finally { setInvoiceSending(false); }
  };

  const loadDashboard = async () => {
    // Parallelize the two independent calls
    const [dashRes, reasonsRes] = await Promise.allSettled([
      api.get("/admin/dashboard"),
      api.get("/admin/orders/cancellation-reasons"),
    ]);
    if (dashRes.status === "fulfilled") setDashboard(dashRes.value.data);
    else toast.error(apiError(dashRes.reason));
    if (reasonsRes.status === "fulfilled") setCancelReasons(reasonsRes.value.data);
  };

  const loadCategories = async () => {
    try {
      const { data } = await api.get("/books/facets");
      setCategories(data.categories || []);
    } catch { /* silent */ }
  };

  // Initial mount — only load what the default tab (dashboard) needs.
  // Other tabs load lazily when the user clicks them.
  useEffect(() => { loadDashboard(); loadCategories(); }, []);

  useEffect(() => {
    if (tab === "orders") loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, ordersPage, orderDateFrom, orderDateTo]);

  useEffect(() => {
    if (tab === "books") loadBooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, loadBooks]);

  const handleCsv = async (file) => {
    if (!file) return;
    setUploading(true);
    setCsvResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/books/csv-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCsvResult(data);
      toast.success(`Inserted ${data.inserted}, updated ${data.updated}`);
      loadBooks();
      loadDashboard();
    } catch (e) { toast.error(apiError(e)); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const openNew = () => { setForm(emptyForm); setEditing(null); setShowForm(true); };
  const openEdit = (b) => {
    setForm({ ...emptyForm, ...b, mrp: b.mrp || 0, out_of_stock: b.out_of_stock_override || false });
    setEditing(b.id); setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const { _discPct, ...clean } = form;
    const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    // Preserve existing stock when the input is blank. `Number("")` = 0
    // which would silently zero out inventory — the user hit this bug.
    const rawStock = clean.stock;
    const stockIsBlank = rawStock === "" || rawStock === null || rawStock === undefined;
    const payload = {
      ...clean,
      price: Number(clean.price),
      mrp: Number(clean.mrp) || null,
      price_usd_override: numOrNull(clean.price_usd_override),
      mrp_usd_override: numOrNull(clean.mrp_usd_override),
      price_gbp_override: numOrNull(clean.price_gbp_override),
      mrp_gbp_override: numOrNull(clean.mrp_gbp_override),
      price_eur_override: numOrNull(clean.price_eur_override),
      mrp_eur_override: numOrNull(clean.mrp_eur_override),
      stock: stockIsBlank ? (editing ? Number((books.find(b => b.id === editing) || {}).stock ?? 0) : 0) : Number(rawStock),
      is_new_arrival: !!clean.is_new_arrival,
      is_featured: !!clean.is_featured,
      out_of_stock: !!clean.out_of_stock,
    };
    try {
      if (editing) {
        await api.put(`/admin/books/${editing}`, payload);
        toast.success("Book updated");
      } else {
        await api.post("/admin/books", payload);
        toast.success("Book created");
      }
      setShowForm(false);
      loadBooks(); loadDashboard();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this book?")) return;
    try {
      await api.delete(`/admin/books/${id}`);
      toast.success("Deleted");
      loadBooks(); loadDashboard();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleFlag = async (id, field, current) => {
    try {
      await api.patch(`/admin/books/${id}/toggle`, { field, value: !current });
      toast.success(`${field.replace(/_/g, ' ')} → ${!current ? 'ON' : 'OFF'}`);
      loadBooks(); loadDashboard();
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadImage = async (bookId, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Only patch the image URL — never resubmit the entire book so stale
      // form state can't accidentally overwrite price/stock/etc.
      await api.patch(`/admin/books/${bookId}/image`, { image_url: data.url });
      toast.success("Cover updated");
      loadBooks();
    } catch (e) { toast.error(apiError(e)); }
  };

  // Upload image inside the Edit/Add form (sets form.image_url without saving)
  const [formUploading, setFormUploading] = useState(false);
  const uploadFormImage = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setFormUploading(true);
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded — click Save to apply");
    } catch (e) { toast.error(apiError(e)); }
    finally { setFormUploading(false); }
  };

  // Discount % helpers (price = mrp - mrp*disc/100)
  const currentDiscount = () => {
    const mrp = Number(form.mrp) || 0;
    const price = Number(form.price) || 0;
    if (mrp <= 0 || price <= 0 || price > mrp) return 0;
    return Math.round(((mrp - price) / mrp) * 100);
  };
  const applyDiscount = (pct) => {
    const mrp = Number(form.mrp) || 0;
    const n = Math.max(0, Math.min(90, Number(pct) || 0));
    if (mrp <= 0) { toast.error("Set MRP first to apply a discount"); return; }
    const newPrice = Math.round(mrp * (1 - n / 100));
    setForm((f) => ({ ...f, price: newPrice }));
  };

  const CANCEL_REASON_PRESETS = [
    "Out of stock",
    "Customer request",
    "Payment failed",
    "Address unreachable",
    "Duplicate order",
    "Suspected fraud",
    "Other",
  ];

  const setOrderStatus = async (id, status) => {
    if (status === "cancelled") {
      const order = orders.find((o) => o.id === id);
      setCancelReasonChoice("Out of stock");
      setCancelReasonOther("");
      setCancelModal({ orderId: id, orderNumber: order?.order_number || "" });
      return;
    }
    try {
      await api.post(`/admin/orders/${id}/status`, { status });
      loadOrders(); loadDashboard();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitCancellation = async () => {
    if (!cancelModal) return;
    const reason = cancelReasonChoice === "Other"
      ? cancelReasonOther.trim()
      : cancelReasonChoice;
    if (!reason) {
      toast.error("Please enter or choose a reason.");
      return;
    }
    setCancelBusy(true);
    try {
      await api.post(`/admin/orders/${cancelModal.orderId}/status`, {
        status: "cancelled",
        cancellation_reason: reason,
      });
      toast.success(`Order ${cancelModal.orderNumber} cancelled`);
      setCancelModal(null);
      loadOrders(); loadDashboard();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCancelBusy(false);
    }
  };

  const downloadSample = () => {
    const rows = [
      ["title","author","publisher","category","subject","class_level","isbn","price","mrp","price_usd","stock","description","image_url","content","book_size"],
      ["Sample Book Title","Author Name","Publisher","History","Modern History","","9788174506726","999","1200","","10","Book description","","Hardbound • 350 pages","24 x 16 cm"],
      ["Foreign Priced Book","Author","Publisher","Art and Archaeology","","","","","","$45","5","Priced in USD; will auto-convert to INR","","",""],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "sample_books.csv"; a.click();
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between border-b-2 border-[#09090b] pb-4 mb-6 gap-3">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c]">GAINA THE PRELUDE</div>
          <h1 className="font-display text-3xl md:text-5xl font-black tracking-tight">Admin Panel</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { key: "dashboard", label: "Dashboard", icon: BarChart3 },
            { key: "books", label: `Books (${dashboard?.total_books ?? books.length})`, icon: Package },
            { key: "orders", label: `Orders (${dashboard?.total_orders ?? orders.length})`, icon: Receipt },
            { key: "manual", label: "Manual Invoice", icon: Receipt },
            { key: "proforma", label: "Proforma", icon: Receipt },
            { key: "library", label: "Library Invoice", icon: Receipt },
            { key: "other-bill", label: "Other Company Bill", icon: Receipt },
            { key: "payments", label: "Payments (PayPal)", icon: CreditCard },
            { key: "analytics", label: "Analytics", icon: Users },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-3 md:px-4 py-2 font-bold text-sm border-2 ${tab === t.key ? "bg-[#09090b] text-white border-[#09090b]" : "border-[#09090b] text-[#09090b] hover:bg-[#f4f4f5]"}`}
                data-testid={`tab-${t.key}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* DASHBOARD TAB */}
      {tab === "dashboard" && dashboard && (
        <div className="space-y-6" data-testid="dashboard-view">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Total Books", value: dashboard.total_books, color: "border-l-[#ea580c]" },
              { label: "Total Orders", value: dashboard.total_orders, color: "border-l-[#facc15]" },
              { label: "Pending", value: dashboard.pending_orders, color: "border-l-[#dc2626]" },
              {
                label: "Revenue",
                value: (
                  <>
                    <div>₹{Math.round(dashboard.revenue_by_currency?.INR || dashboard.total_revenue || 0).toLocaleString("en-IN")}</div>
                    <div className="text-sm text-[#16a34a] font-bold">${Math.round(dashboard.revenue_by_currency?.USD || 0).toLocaleString("en-US")}</div>
                  </>
                ),
                color: "border-l-[#16a34a]",
              },
              { label: "New Arrivals", value: dashboard.new_arrivals, color: "border-l-[#3b82f6]" },
              { label: "Featured", value: dashboard.featured, color: "border-l-[#a855f7]" },
              { label: "Out of Stock", value: dashboard.out_of_stock, color: "border-l-[#ef4444]" },
              { label: "Total Visits", value: dashboard.total_visits, color: "border-l-[#0ea5e9]" },
            ].map((s) => (
              <div key={s.label} className={`bg-white border border-[#e7e5e4] border-l-4 ${s.color} p-4`} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}>
                <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#71717a]">{s.label}</div>
                <div className="mt-1 font-display text-2xl md:text-3xl font-black text-[#09090b]">{s.value}</div>
              </div>
            ))}
          </div>

          {/* SEO ping card — sends the entire catalog to IndexNow (Bing/Yandex/Naver/Seznam) */}
          <div className="border-2 border-[#09090b] bg-white p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="seo-ping-card">
            <div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-1 flex items-center gap-2">
                <Radar className="w-4 h-4" /> Search Engine Indexing
              </div>
              <div className="font-display text-lg font-black text-[#09090b]">Ping IndexNow with all book URLs</div>
              <div className="text-xs text-[#71717a] mt-1">
                Notifies Bing, Yandex, Naver, Seznam &amp; DuckDuckGo instantly. Also sitemap-pings Google.
                Run this after a big CSV import to speed up fresh indexing.
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-2">
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm("Rewrite all book image_urls that still point at the stale preview host to a relative /api/uploads/… path? Safe & idempotent.")) return;
                  try {
                    const r = await api.post("/admin/seo/fix-image-urls");
                    toast.success(`Migrated ${r.data.rewrote} image URLs (${r.data.remaining_stale} remain)`);
                  } catch (e) { toast.error(apiError(e)); }
                }}
                className="inline-flex items-center gap-2 px-4 py-2.5 font-bold text-sm bg-white text-[#09090b] border-2 border-[#09090b] hover:bg-[#f4f4f5] transition-colors whitespace-nowrap"
                data-testid="btn-fix-image-urls"
              >
                Fix Image URLs
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const r = await api.post("/admin/seo/ping-sitemap");
                    toast.success(`Pinged ${r.data.pinged_urls} URLs → search engines`);
                  } catch (e) { toast.error(apiError(e)); }
                }}
                className="inline-flex items-center gap-2 px-4 py-2.5 font-bold text-sm bg-[#09090b] text-white border-2 border-[#09090b] hover:bg-[#ea580c] hover:border-[#ea580c] transition-colors whitespace-nowrap"
                data-testid="btn-ping-sitemap"
              >
                <Send className="w-4 h-4" /> Ping Search Engines
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-[#e7e5e4] bg-white p-5">
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-3">Recent Orders</div>
              <table className="w-full text-sm">
                <thead className="text-left border-b border-[#e7e5e4]">
                  <tr><th className="pb-2">Order</th><th className="pb-2">Customer</th><th className="pb-2 text-right">Total</th><th className="pb-2">Status</th></tr>
                </thead>
                <tbody>
                  {(dashboard.recent_orders || []).map((o) => {
                    const oSym = (o.currency || "INR") === "USD" ? "$" : "\u20b9";
                    return (
                      <tr key={o.id} className="border-b border-[#f4f4f5]">
                        <td className="py-2 font-mono text-xs">{o.order_number}</td>
                        <td className="py-2">{o.customer} <span className="text-[#71717a]">• {o.phone}</span></td>
                        <td className="py-2 text-right font-bold">{oSym}{o.total}</td>
                        <td className="py-2"><span className="text-xs font-bold uppercase">{o.status}</span></td>
                      </tr>
                    );
                  })}
                  {(dashboard.recent_orders || []).length === 0 && (
                    <tr><td colSpan={4} className="py-6 text-center text-[#71717a]">No orders yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="border border-[#e7e5e4] bg-white p-5">
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-3">Order Status Breakdown</div>
              <div className="space-y-2">
                {Object.entries(dashboard.status_breakdown || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className="font-semibold uppercase text-xs tracking-wider">{k}</span>
                    <span className="font-mono font-bold">{v}</span>
                  </div>
                ))}
                {Object.keys(dashboard.status_breakdown || {}).length === 0 && (
                  <div className="text-[#71717a] text-sm">No orders yet</div>
                )}
              </div>
            </div>
          </div>

          {/* Cancelled Order Reasons */}
          <div className="border border-[#e7e5e4] bg-white p-5" data-testid="cancel-reasons-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#dc2626]">Cancelled Order Reasons</div>
                <div className="text-[11px] text-[#71717a] mt-0.5">
                  {cancelReasons?.total_cancelled ? `${cancelReasons.total_cancelled} cancelled orders total` : "No cancellations yet"}
                </div>
              </div>
            </div>
            {cancelReasons?.reasons?.length > 0 ? (
              <div style={{ width: "100%", minHeight: 260, height: 260 }} data-testid="cancel-reasons-chart">
                <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                  <BarChart
                    data={cancelReasons.reasons}
                    layout="vertical"
                    margin={{ top: 4, right: 24, bottom: 4, left: 8 }}
                  >
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#71717a" }} />
                    <YAxis
                      type="category"
                      dataKey="reason"
                      width={180}
                      tick={{ fontSize: 11, fill: "#09090b" }}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(220,38,38,0.08)" }}
                      contentStyle={{ border: "2px solid #09090b", borderRadius: 0, fontSize: 12 }}
                      formatter={(v) => [`${v} orders`, "Count"]}
                    />
                    <Bar dataKey="count" radius={0}>
                      {cancelReasons.reasons.map((_, i) => (
                        <Cell key={i} fill={i === 0 ? "#dc2626" : i === 1 ? "#ea580c" : "#facc15"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-[#71717a] text-sm py-8 text-center border border-dashed border-[#e7e5e4]">
                Cancellation reasons will appear here once you cancel an order with a reason.
              </div>
            )}
          </div>
        </div>
      )}

      {/* BOOKS TAB */}
      {tab === "books" && (
        <>
          {/* CSV Upload */}
          <div className="border-2 border-[#09090b] p-6 mb-6" data-testid="csv-upload-section">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mb-4">
              <div>
                <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c]">Bulk Import / Update</div>
                <h2 className="font-display text-2xl font-black">CSV / Excel Upload</h2>
                <div className="text-xs text-[#52525b] mt-1">.csv, .xlsx, .xls supported • auto-detects INR / USD ($) / GBP (£) / EUR (€) columns • matched by ISBN (or title+publisher)</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={downloadSample} className="inline-flex items-center gap-2 border-2 border-[#09090b] px-3 py-2 font-bold text-xs" data-testid="download-sample-csv">
                  <Download className="w-3.5 h-3.5" /> Sample CSV
                </button>
                <button
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/admin/books/auto-covers");
                      toast.success(`Auto-fetched covers for ${data.updated} books`);
                      loadBooks();
                    } catch (e) { toast.error(apiError(e)); }
                  }}
                  className="inline-flex items-center gap-2 bg-[#09090b] text-white px-3 py-2 font-bold text-xs"
                  data-testid="auto-covers-btn">
                  Auto-fetch Covers via ISBN
                </button>
              </div>
            </div>
            <label className="border-2 border-dashed border-[#09090b] p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-[#f4f4f5]" data-testid="csv-dropzone">
              <Upload className="w-7 h-7 mb-2 text-[#ea580c]" />
              <div className="font-bold text-sm">{uploading ? "Uploading…" : "Click or drop CSV / Excel file"}</div>
              <div className="text-[11px] text-[#71717a] mt-2 flex flex-wrap gap-x-3 gap-y-1 justify-center">
                <span>• Max <b>50,000 titles</b></span>
                <span>• Max <b>20 MB</b></span>
                <span>• Auto-covers via ISBN</span>
              </div>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => handleCsv(e.target.files?.[0])} data-testid="csv-file-input" />
            </label>
            {csvResult && (
              <div className="mt-4 border border-border p-4 bg-[#f4f4f5] text-sm">
                <div className="font-bold">Import complete</div>
                <div>Inserted: {csvResult.inserted} • Updated: {csvResult.updated} • Rows: {csvResult.total_rows}</div>
                {csvResult.errors?.length > 0 && (
                  <div className="mt-2 text-[#dc2626]">
                    <div className="font-bold">{csvResult.errors.length} errors</div>
                    <details className="text-xs mt-1"><summary className="cursor-pointer">Show</summary>
                      <ul className="list-disc pl-5">{csvResult.errors.slice(0, 20).map((er, i) => <li key={i}>{er}</li>)}</ul>
                    </details>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Books list */}
          <div className="border border-border">
            <div className="p-4 border-b border-border flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2 flex-1">
                <input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} onKeyDown={(e) => e.key === "Enter" && loadBooks()} placeholder="Search ISBN / title / author / publisher…" className="border-2 border-[#09090b] px-3 py-2 text-sm outline-none flex-1 min-w-[240px]" data-testid="admin-search-input" />
                <select value={filterCategory} onChange={(e) => { setFilterCategory(e.target.value); setPage(0); }} className="border-2 border-[#09090b] px-2 py-2 text-sm" data-testid="admin-filter-category">
                  <option value="">All Categories</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={filterFlag} onChange={(e) => { setFilterFlag(e.target.value); setPage(0); }} className="border-2 border-[#09090b] px-2 py-2 text-sm" data-testid="admin-filter-flag">
                  <option value="">All Books</option>
                  <option value="new_arrival">New Arrivals</option>
                  <option value="featured">Featured</option>
                  <option value="in_stock">In Stock only</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              </div>
              <button onClick={openNew} className="inline-flex items-center gap-2 bg-[#ea580c] text-white font-bold px-4 py-2 text-sm" data-testid="add-book-btn">
                <Plus className="w-4 h-4" /> Add Book
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-books-table">
                <thead className="bg-[#09090b] text-white text-left">
                  <tr>
                    <th className="p-3">Book</th>
                    <th className="p-3 hidden md:table-cell">Category</th>
                    <th className="p-3 text-right">Price</th>
                    <th className="p-3 text-right">Stock</th>
                    <th className="p-3">Flags</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {books.map((b) => (
                    <tr key={b.id} className="border-b border-border align-top">
                      <td className="p-3">
                        <div className="flex gap-3 items-start">
                          {b.image_url ? (
                            <img src={b.image_url} alt="" className="w-10 h-14 object-cover border border-border shrink-0" />
                          ) : (
                            <div className="w-10 h-14 bg-[#f4f4f5] border border-border shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div className="font-bold truncate max-w-xs">{b.title}</div>
                            <div className="text-xs text-[#52525b]">{b.author || "—"}</div>
                            <div className="text-[10px] text-[#71717a] font-mono">{b.isbn || "no ISBN"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 hidden md:table-cell text-xs">{b.category || "—"}</td>
                      <td className="p-3 text-right">
                        <div className="font-bold">₹{b.price}</div>
                        {b.mrp && b.mrp > b.price && <div className="text-xs text-[#52525b] line-through">₹{b.mrp}</div>}
                        {b.price_usd > 0 && (
                          <div className="text-[10px] text-[#16a34a] font-bold mt-0.5">${b.price_usd}{b.price_usd_override ? " •set" : ""}</div>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <span className={`font-bold ${b.stock <= 0 ? 'text-[#dc2626]' : ''}`}>{b.stock}</span>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-col gap-1">
                          <button onClick={() => toggleFlag(b.id, 'is_new_arrival', b.is_new_arrival)} className={`text-[10px] px-2 py-0.5 border font-bold tracking-wider uppercase ${b.is_new_arrival ? 'bg-[#3b82f6] text-white border-[#3b82f6]' : 'border-[#e4e4e7] text-[#71717a]'}`} data-testid={`toggle-new-${b.id}`}>
                            <Sparkles className="w-2.5 h-2.5 inline mr-0.5" />New
                          </button>
                          <button onClick={() => toggleFlag(b.id, 'is_featured', b.is_featured)} className={`text-[10px] px-2 py-0.5 border font-bold tracking-wider uppercase ${b.is_featured ? 'bg-[#a855f7] text-white border-[#a855f7]' : 'border-[#e4e4e7] text-[#71717a]'}`} data-testid={`toggle-featured-${b.id}`}>
                            <Star className="w-2.5 h-2.5 inline mr-0.5" />Feat
                          </button>
                          <button onClick={() => toggleFlag(b.id, 'out_of_stock', b.out_of_stock_override)} className={`text-[10px] px-2 py-0.5 border font-bold tracking-wider uppercase ${b.out_of_stock_override ? 'bg-[#dc2626] text-white border-[#dc2626]' : 'border-[#e4e4e7] text-[#71717a]'}`} data-testid={`toggle-oos-${b.id}`}>
                            <PackageX className="w-2.5 h-2.5 inline mr-0.5" />OOS
                          </button>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1 justify-end items-start">
                          <label className="p-1.5 border border-border cursor-pointer hover:bg-[#f4f4f5]" title="Change cover">
                            <Upload className="w-3.5 h-3.5" />
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(b.id, e.target.files?.[0])} />
                          </label>
                          <button onClick={() => openEdit(b)} className="p-1.5 border border-border hover:bg-[#f4f4f5]" title="Edit"><Edit3 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => remove(b.id)} className="p-1.5 border border-border hover:bg-[#dc2626] hover:text-white" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {books.length === 0 && (
                    <tr><td colSpan={6} className="p-8 text-center text-[#52525b]">No books match your filter</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="p-4 border-t border-border flex items-center justify-between text-sm">
              <div className="text-[#52525b]">
                Showing <b>{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)}</b> of <b>{total}</b>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="p-2 border border-border disabled:opacity-40" data-testid="page-prev"><ChevronLeft className="w-4 h-4" /></button>
                <span className="px-3 font-bold">Page {page + 1} / {totalPages}</span>
                <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} className="p-2 border border-border disabled:opacity-40" data-testid="page-next"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ORDERS TAB */}
      {tab === "orders" && (
        <div className="space-y-4">
          {/* Filters + Actions Bar */}
          <div className="border-2 border-[#09090b] bg-[#fafafa] p-4">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
              <div className="md:col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#52525b] mb-1">Search</div>
                <input value={orderSearchQ} onChange={(e) => setOrderSearchQ(e.target.value)} placeholder="Order number, customer, phone…" className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="order-search" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#52525b] mb-1">From</div>
                <input type="date" value={orderDateFrom} onChange={(e) => setOrderDateFrom(e.target.value)} className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="order-date-from" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#52525b] mb-1">To</div>
                <input type="date" value={orderDateTo} onChange={(e) => setOrderDateTo(e.target.value)} className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="order-date-to" />
              </div>
              <button onClick={() => { setOrdersPage(0); loadOrders(); }} className="bg-[#09090b] text-white font-bold text-xs uppercase tracking-wider px-4 py-2" data-testid="apply-order-filters">Apply</button>
              <button onClick={() => { setOrderDateFrom(""); setOrderDateTo(""); setOrderSearchQ(""); setOrdersPage(0); }} className="border-2 border-[#09090b] font-bold text-xs uppercase tracking-wider px-4 py-2 bg-white" data-testid="clear-order-filters">Clear</button>
            </div>

            {/* Download / Print Actions */}
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t-2 border-[#e4e4e7]">
              <button
                onClick={() => {
                  const d = orderDateFrom || new Date().toISOString().slice(0, 10);
                  downloadPdf(`/admin/orders/daily.pdf?date=${d}`, `daily-orders-${d}.pdf`);
                }}
                className="bg-[#ea580c] text-white font-bold text-xs uppercase tracking-wider px-3 py-2 hover:bg-[#c2410c]"
                data-testid="download-daily-pdf"
              >📄 Download Daily Print</button>
              <button
                onClick={() => downloadPdf(`/admin/statement.pdf?currency=INR&status=confirmed${orderDateFrom ? `&date_from=${orderDateFrom}` : ""}${orderDateTo ? `&date_to=${orderDateTo}` : ""}`, `confirmed-statement-INR.pdf`)}
                className="border-2 border-[#16a34a] text-[#16a34a] font-bold text-xs uppercase tracking-wider px-3 py-2 bg-white hover:bg-[#dcfce7]"
                data-testid="download-stmt-confirmed-inr"
              >✓ INR Confirmed</button>
              <button
                onClick={() => downloadPdf(`/admin/statement.pdf?currency=USD&status=confirmed${orderDateFrom ? `&date_from=${orderDateFrom}` : ""}${orderDateTo ? `&date_to=${orderDateTo}` : ""}`, `confirmed-statement-USD.pdf`)}
                className="border-2 border-[#16a34a] text-[#16a34a] font-bold text-xs uppercase tracking-wider px-3 py-2 bg-white hover:bg-[#dcfce7]"
                data-testid="download-stmt-confirmed-usd"
              >✓ USD Confirmed</button>
              <button
                onClick={() => downloadPdf(`/admin/statement.pdf?currency=INR&status=cancelled${orderDateFrom ? `&date_from=${orderDateFrom}` : ""}${orderDateTo ? `&date_to=${orderDateTo}` : ""}`, `cancelled-statement-INR.pdf`)}
                className="border-2 border-[#dc2626] text-[#dc2626] font-bold text-xs uppercase tracking-wider px-3 py-2 bg-white hover:bg-[#fee2e2]"
                data-testid="download-stmt-cancelled-inr"
              >✗ INR Cancelled</button>
              <button
                onClick={() => downloadPdf(`/admin/statement.pdf?currency=USD&status=cancelled${orderDateFrom ? `&date_from=${orderDateFrom}` : ""}${orderDateTo ? `&date_to=${orderDateTo}` : ""}`, `cancelled-statement-USD.pdf`)}
                className="border-2 border-[#dc2626] text-[#dc2626] font-bold text-xs uppercase tracking-wider px-3 py-2 bg-white hover:bg-[#fee2e2]"
                data-testid="download-stmt-cancelled-usd"
              >✗ USD Cancelled</button>
            </div>
          </div>

          {/* Revenue-by-Day Widget */}
          {revenueRows.length > 0 && (
            <div className="border-2 border-[#e4e4e7]">
              <div className="bg-[#09090b] text-white p-3 text-xs font-bold tracking-[0.2em] uppercase">Revenue by Day</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#fafafa]">
                    <tr>
                      <th className="p-2 text-left">Date</th>
                      <th className="p-2 text-left">Currency</th>
                      <th className="p-2 text-right">Orders</th>
                      <th className="p-2 text-right">Revenue</th>
                      <th className="p-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenueRows.map((r, i) => (
                      <tr key={i} className="border-t border-[#e4e4e7]">
                        <td className="p-2 font-mono">{r.day}</td>
                        <td className="p-2">
                          <span className={`inline-block px-2 py-0.5 text-[10px] font-bold ${r.currency === "USD" ? "bg-[#e0f2fe] text-[#075985]" : "bg-[#dcfce7] text-[#166534]"}`}>{r.currency}</span>
                        </td>
                        <td className="p-2 text-right">{r.orders}</td>
                        <td className="p-2 text-right font-bold">{r.currency === "USD" ? "$" : "₹"}{r.revenue}</td>
                        <td className="p-2 text-center">
                          <button onClick={() => downloadPdf(`/admin/orders/daily.pdf?date=${r.day}`, `daily-${r.day}.pdf`)} className="text-[#ea580c] font-bold text-[10px] uppercase tracking-wider hover:underline" data-testid={`revenue-day-pdf-${r.day}`}>📄 PDF</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Orders Table */}
          <div className="border border-border">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-orders-table">
                <thead className="bg-[#09090b] text-white text-left">
                  <tr>
                    <th className="p-3">Order #</th>
                    <th className="p-3">Customer</th>
                    <th className="p-3">Contact</th>
                    <th className="p-3">Items</th>
                    <th className="p-3 text-right">Total</th>
                    <th className="p-3">Payment</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Documents</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const cur = (o.currency || "INR").toUpperCase();
                    const sym = ({ USD: "$", EUR: "\u20ac", GBP: "\u00a3", INR: "\u20b9" })[cur] || "\u20b9";
                    // Compute alt-currency estimate: INR ↔ USD using the same tiered logic as customer view
                    const inrVal = cur === "INR" ? Number(o.total || 0) : Number(o.total || 0) * 85;
                    const usdTier = (function(v){
                      if (!v || v <= 0) return 0;
                      if (v < 1000) return Math.max(1, Math.round(v * 0.06));
                      if (v < 2000) return 70;
                      if (v < 3000) return Math.round(100 + ((v - 2000) / 1000) * 25);
                      if (v < 4000) return Math.round(125 + ((v - 3000) / 1000) * 25);
                      if (v <= 5000) return Math.round(180 + ((v - 4000) / 1000) * 40);
                      return Math.round(220 + ((v - 5000) / 200) * 8);
                    })(inrVal);
                    const altStr = cur === "INR" ? `\u2248 $${usdTier}` : `\u2248 \u20b9${Math.round(Number(o.total || 0) * 85)}`;
                    return (
                      <tr key={o.id} className="border-b border-border align-top">
                        <td className="p-3">
                          <div className="font-mono font-bold">{o.order_number}</div>
                          <div className="text-[10px] text-[#71717a] mt-1">{o.created_at?.slice(0, 16).replace('T',' ')}</div>
                          {o.is_guest && <span className="inline-block mt-1 text-[9px] font-bold bg-[#fef3c7] text-[#92400e] px-1.5 py-0.5">GUEST</span>}
                        </td>
                        <td className="p-3">
                          <div className="font-semibold">{o.shipping?.name || o.user_name}</div>
                          <div className="text-xs text-[#52525b]">{o.shipping?.city}, {o.shipping?.state} {o.shipping?.pincode}</div>
                          <div className="text-xs text-[#52525b]">{o.shipping?.address}</div>
                        </td>
                        <td className="p-3 text-xs">
                          <div className="font-mono">{o.shipping?.phone}</div>
                          <a href={`mailto:${o.user_email}`} className="text-[#ea580c] hover:underline">{o.user_email}</a>
                        </td>
                        <td className="p-3">
                          <details className="text-xs">
                            <summary className="cursor-pointer font-bold">{o.items?.length || 0} items</summary>
                            <ul className="mt-2 text-[10px] space-y-1">
                              {(o.items || []).map((it, i) => (
                                <li key={i} className="border-l-2 border-[#ea580c] pl-2">
                                  <div className="font-semibold">{it.title}</div>
                                  <div className="text-[#71717a]">ISBN: {it.isbn || "—"} · Qty {it.quantity} · {sym}{it.subtotal}</div>
                                </li>
                              ))}
                            </ul>
                          </details>
                        </td>
                        <td className="p-3 text-right font-bold">
                          <div>{sym}{o.total}</div>
                          <div className="text-[10px] font-semibold text-[#16a34a] mt-0.5">{altStr}</div>
                        </td>
                        <td className="p-3 text-[10px]">
                          <div className="font-bold uppercase">{(o.payment_method || "bank_transfer").replace("_", " ")}</div>
                          <div className={`inline-block mt-1 px-1.5 py-0.5 font-bold ${o.payment_status === "paid" ? "bg-[#dcfce7] text-[#166534]" : "bg-[#fef3c7] text-[#92400e]"}`}>{o.payment_status || "unpaid"}</div>
                        </td>
                        <td className="p-3">
                          <select value={o.status} onChange={(e) => setOrderStatus(o.id, e.target.value)} className="border border-border px-2 py-1 text-xs font-semibold" data-testid={`order-status-${o.id}`}>
                            <option>pending</option><option>confirmed</option><option>processing</option><option>shipped</option><option>delivered</option><option>cancelled</option>
                          </select>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col gap-1">
                            <button onClick={() => downloadPdf(`/admin/orders/${o.id}/invoice.pdf`, `invoice-${o.order_number.replace('/','_')}.pdf`)} className="text-[#ea580c] font-bold text-[10px] uppercase tracking-wider hover:underline text-left" data-testid={`invoice-pdf-${o.id}`}>Invoice PDF</button>
                            <button onClick={() => downloadPdf(`/admin/orders/${o.id}/post-slip.pdf`, `post-slip-${o.order_number.replace('/','_')}.pdf`)} className="text-[#09090b] font-bold text-[10px] uppercase tracking-wider hover:underline text-left" data-testid={`post-slip-pdf-${o.id}`}>Post Slip</button>
                            <button
                              onClick={() => openInvoiceEmail(o)}
                              disabled={!o.user_email}
                              className="text-[#16a34a] font-bold text-[10px] uppercase tracking-wider hover:underline text-left flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                              title={o.user_email ? `Email invoice to ${o.user_email}` : "No customer email"}
                              data-testid={`email-invoice-${o.id}`}
                            >
                              <Mail className="w-3 h-3" /> Email Invoice
                            </button>
                            {o.invoice_emailed_at && (
                              <div className="text-[9px] text-[#16a34a]" title={o.invoice_emailed_at}>✓ Sent</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                {orders.length === 0 && (
                  <tr><td colSpan={8} className="p-8 text-center text-[#52525b]">No orders yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Orders pagination */}
          {ordersTotal > ORDERS_PAGE_SIZE && (
            <div className="p-3 border-t border-border flex items-center justify-between bg-[#fafafa]" data-testid="orders-pagination">
              <div className="text-xs text-[#52525b]">
                Showing <b>{ordersPage * ORDERS_PAGE_SIZE + 1}</b>–<b>{Math.min((ordersPage + 1) * ORDERS_PAGE_SIZE, ordersTotal)}</b> of <b>{ordersTotal}</b>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setOrdersPage((p) => Math.max(0, p - 1))}
                  disabled={ordersPage === 0}
                  className="px-3 py-1 border-2 border-[#09090b] text-xs font-bold disabled:opacity-40"
                  data-testid="orders-prev"
                >
                  <ChevronLeft className="w-3 h-3 inline" /> Prev
                </button>
                <button
                  type="button"
                  onClick={() => setOrdersPage((p) => p + 1)}
                  disabled={(ordersPage + 1) * ORDERS_PAGE_SIZE >= ordersTotal}
                  className="px-3 py-1 border-2 border-[#09090b] text-xs font-bold disabled:opacity-40"
                  data-testid="orders-next"
                >
                  Next <ChevronRight className="w-3 h-3 inline" />
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      )}

      {tab === "manual" && (
        <InvoiceDocsPanel kind="MI" title="Manual Invoices" />
      )}

      {tab === "proforma" && (
        <InvoiceDocsPanel kind="PI" title="Proforma Invoices" />
      )}

      {tab === "library" && (
        <LibraryInvoicePanel />
      )}

      {tab === "other-bill" && (
        <LibraryInvoicePanel
          kind="OB"
          endpoint="/admin/other-bills"
          title="Other Company Bills"
          subtitle="Bill on behalf of a different company · Company Override required"
          requireCompanyOverride
        />
      )}

      {tab === "payments" && (
        <PaymentsPanel />
      )}

      {tab === "analytics" && (
        <AnalyticsPanel />
      )}


      {/* Cancellation Reason modal */}
      {cancelModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !cancelBusy && setCancelModal(null)} data-testid="cancel-reason-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md border-2 border-[#09090b]">
            <div className="p-4 border-b-2 border-[#09090b] flex items-center justify-between bg-[#fef2f2]">
              <div>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#dc2626]">Cancel Order</div>
                <div className="font-display text-lg font-black">{cancelModal.orderNumber || "—"}</div>
              </div>
              <button type="button" onClick={() => !cancelBusy && setCancelModal(null)} className="p-1" data-testid="cancel-modal-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-[11px] text-[#71717a] leading-relaxed">
                Choose a cancellation reason. This is stored for internal analytics only and is <b>never shown to the customer</b>.
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#71717a] mb-1">Reason *</div>
                <select
                  value={cancelReasonChoice}
                  onChange={(e) => setCancelReasonChoice(e.target.value)}
                  disabled={cancelBusy}
                  className="w-full border-2 border-[#09090b] px-3 py-2 text-sm font-semibold outline-none"
                  data-testid="cancel-reason-select"
                >
                  {CANCEL_REASON_PRESETS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              {cancelReasonChoice === "Other" && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#71717a] mb-1">Custom reason *</div>
                  <input
                    autoFocus
                    value={cancelReasonOther}
                    onChange={(e) => setCancelReasonOther(e.target.value)}
                    maxLength={200}
                    placeholder="e.g. Buyer moved country"
                    disabled={cancelBusy}
                    className="w-full border-2 border-[#e4e4e7] focus:border-[#dc2626] px-3 py-2 text-sm outline-none"
                    data-testid="cancel-reason-other"
                  />
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setCancelModal(null)}
                  disabled={cancelBusy}
                  className="px-4 py-2 text-sm font-bold border-2 border-[#e4e4e7] hover:border-[#09090b] disabled:opacity-50"
                  data-testid="cancel-modal-abort"
                >
                  Never mind
                </button>
                <button
                  type="button"
                  onClick={submitCancellation}
                  disabled={cancelBusy}
                  className="px-4 py-2 text-sm font-bold bg-[#dc2626] hover:bg-[#b91c1c] text-white disabled:opacity-60"
                  data-testid="cancel-modal-submit"
                >
                  {cancelBusy ? "Cancelling…" : "Confirm Cancellation"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Email Invoice preview modal */}
      {invoiceModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={closeInvoiceModal} data-testid="email-invoice-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-4xl border-2 border-[#09090b] my-8 flex flex-col">
            <div className="p-4 border-b-2 border-[#09090b] flex items-center justify-between bg-[#fff7ed]">
              <div>
                <div className="font-display text-xl font-black">Email Invoice · {invoiceModal.order.order_number}</div>
                <div className="text-xs text-[#52525b] mt-0.5">
                  Preview below. Click <b>Send to Customer</b> to email the PDF as an attachment.
                </div>
              </div>
              <button type="button" onClick={closeInvoiceModal} className="p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 bg-[#f4f4f5]">
              <iframe
                title="invoice-preview"
                src={invoiceModal.pdfUrl}
                className="w-full h-[600px] bg-white border border-border"
                data-testid="invoice-preview-frame"
              />
            </div>
            <div className="p-4 border-t border-border flex items-center justify-between flex-wrap gap-3">
              <div className="text-sm">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#71717a]">Recipient</div>
                <div className="font-semibold" data-testid="invoice-email-recipient">{invoiceModal.order.user_email}</div>
                <div className="text-[10px] text-[#71717a]">{invoiceModal.order.user_name || invoiceModal.order.shipping?.name}</div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={closeInvoiceModal}
                  className="border-2 border-[#09090b] font-bold px-4 py-2 text-sm"
                  data-testid="invoice-email-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={sendInvoiceEmail}
                  disabled={invoiceSending}
                  className="bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-bold px-5 py-2 text-sm flex items-center gap-2"
                  data-testid="invoice-email-send"
                >
                  <Send className="w-4 h-4" /> {invoiceSending ? "Sending…" : "Send to Customer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Book form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white w-full max-w-3xl border-2 border-[#09090b] my-8" data-testid="book-form-modal">
            <div className="p-4 border-b-2 border-[#09090b] flex items-center justify-between">
              <div className="font-display text-2xl font-black">{editing ? "Edit Book" : "Add Book"}</div>
              <button type="button" onClick={() => setShowForm(false)} className="p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["title","Title *","text"], ["author","Author","text"], ["publisher","Publisher (hidden on frontend for select imprints)","text"],
                ["subject","Subject","text"], ["class_level","Class Level","text"],
                ["isbn","ISBN","text"], ["price","Price (INR)","number"], ["mrp","MRP","number"],
                ["stock","Stock","number"],
              ].map(([k, label, type]) => (
                <label key={k} className="text-xs">
                  <div className="font-bold uppercase tracking-wider text-[#52525b] mb-1">{label}</div>
                  <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: type === "number" ? e.target.value : e.target.value })} type={type} className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid={`form-${k}`} />
                </label>
              ))}

              {/* Category — dropdown from existing categories (prevents typo duplicates) */}
              <label className="text-xs">
                <div className="font-bold uppercase tracking-wider text-[#52525b] mb-1 flex items-center justify-between">
                  <span>Category</span>
                  <span className="text-[10px] font-normal text-[#71717a]">choose from list</span>
                </div>
                <select
                  value={form.category || ""}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm bg-white"
                  data-testid="form-category"
                >
                  <option value="">— Select Category —</option>
                  {[
                    "Art and Archaeology",
                    "Architecture & Building",
                    "Biography and Autobiography",
                    "Botany",
                    "Buddhism",
                    "Chemical Petroleum",
                    "Children Books",
                    "Coffee Table Books",
                    "Cookery",
                    "General and Reference",
                    "History",
                    "Home Decoration and Furniture Designing",
                    "Law",
                    "Old and Rare Books",
                    "Performing Arts",
                    "Philosophy and Religion",
                    "Physics",
                    "Religion & Spirituality",
                    "Science and Technology",
                    "Social Science",
                    "Zoology",
                    ...(categories || []).filter(c => c && ![
                      "Art and Archaeology","Architecture & Building","Biography and Autobiography","Botany","Buddhism","Chemical Petroleum","Children Books","Coffee Table Books","Cookery","General and Reference","History","Home Decoration and Furniture Designing","Law","Old and Rare Books","Performing Arts","Philosophy and Religion","Physics","Religion & Spirituality","Science and Technology","Social Science","Zoology"
                    ].includes(c)),
                  ].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>

              {/* Discount % quick apply */}
              <label className="text-xs">
                <div className="font-bold uppercase tracking-wider text-[#ea580c] mb-1">Discount % (auto-calc price)</div>
                <div className="flex gap-1">
                  <input
                    type="number"
                    min="0"
                    max="90"
                    placeholder={`current ${currentDiscount()}%`}
                    onChange={(e) => setForm((f) => ({ ...f, _discPct: e.target.value }))}
                    value={form._discPct ?? ""}
                    className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm"
                    data-testid="form-discount-pct"
                  />
                  <button
                    type="button"
                    onClick={() => applyDiscount(form._discPct)}
                    className="bg-[#09090b] text-white font-bold px-3 text-xs whitespace-nowrap"
                    data-testid="form-discount-apply"
                  >Apply</button>
                </div>
                <div className="mt-1 text-[10px] text-[#71717a]">MRP ₹{form.mrp || 0} → Price ₹{form.price || 0} (Save {currentDiscount()}%)</div>
              </label>

              {/* Multi-currency price/MRP overrides — blank = auto-calc */}
              <div className="md:col-span-2 border-2 border-[#e4e4e7] bg-[#fafafa] p-3">
                <div className="text-xs font-bold uppercase tracking-wider text-[#16a34a] mb-2">Multi-Currency Overrides (blank = auto tier from INR)</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <label className="text-xs">
                    <div className="font-bold text-[#52525b] mb-1">Price $ USD</div>
                    <input type="number" step="0.01" value={form.price_usd_override ?? ""} onChange={(e) => setForm({ ...form, price_usd_override: e.target.value === "" ? null : Number(e.target.value) })} className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="form-price-usd" placeholder="auto" />
                  </label>
                  <label className="text-xs">
                    <div className="font-bold text-[#52525b] mb-1">Price £ GBP</div>
                    <input type="number" step="0.01" value={form.price_gbp_override ?? ""} onChange={(e) => setForm({ ...form, price_gbp_override: e.target.value === "" ? null : Number(e.target.value) })} className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="form-price-gbp" placeholder="auto" />
                  </label>
                  <label className="text-xs">
                    <div className="font-bold text-[#52525b] mb-1">Price € EUR</div>
                    <input type="number" step="0.01" value={form.price_eur_override ?? ""} onChange={(e) => setForm({ ...form, price_eur_override: e.target.value === "" ? null : Number(e.target.value) })} className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="form-price-eur" placeholder="auto" />
                  </label>
                  <label className="text-xs">
                    <div className="font-bold text-[#52525b] mb-1">MRP $ USD</div>
                    <input type="number" step="0.01" value={form.mrp_usd_override ?? ""} onChange={(e) => setForm({ ...form, mrp_usd_override: e.target.value === "" ? null : Number(e.target.value) })} className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="form-mrp-usd" placeholder="auto" />
                  </label>
                  <label className="text-xs">
                    <div className="font-bold text-[#52525b] mb-1">MRP £ GBP</div>
                    <input type="number" step="0.01" value={form.mrp_gbp_override ?? ""} onChange={(e) => setForm({ ...form, mrp_gbp_override: e.target.value === "" ? null : Number(e.target.value) })} className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="form-mrp-gbp" placeholder="auto" />
                  </label>
                  <label className="text-xs">
                    <div className="font-bold text-[#52525b] mb-1">MRP € EUR</div>
                    <input type="number" step="0.01" value={form.mrp_eur_override ?? ""} onChange={(e) => setForm({ ...form, mrp_eur_override: e.target.value === "" ? null : Number(e.target.value) })} className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="form-mrp-eur" placeholder="auto" />
                  </label>
                </div>
              </div>

              <label className="text-xs">
                <div className="font-bold uppercase tracking-wider text-[#52525b] mb-1">Book Size</div>
                <input value={form.book_size || ""} onChange={(e) => setForm({ ...form, book_size: e.target.value })} className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="form-book_size" />
              </label>

              {/* Image URL + Upload */}
              <label className="text-xs md:col-span-2">
                <div className="font-bold uppercase tracking-wider text-[#52525b] mb-1 flex items-center justify-between">
                  <span>Book Cover</span>
                  <label className="inline-flex items-center gap-1 bg-[#ea580c] text-white px-3 py-1 cursor-pointer text-[10px] font-bold uppercase tracking-wider" data-testid="form-upload-image-btn">
                    <Upload className="w-3 h-3" />
                    {formUploading ? "Uploading…" : "Upload Image"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadFormImage(e.target.files?.[0])} disabled={formUploading} />
                  </label>
                </div>
                <div className="flex gap-3 items-start">
                  {form.image_url && (
                    <img src={form.image_url} alt="preview" className="w-20 h-24 object-cover border-2 border-[#e4e4e7] flex-shrink-0" data-testid="form-image-preview" />
                  )}
                  <input value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://... or click Upload Image" className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="form-image_url" />
                </div>
              </label>
              <label className="text-xs md:col-span-2">
                <div className="font-bold uppercase tracking-wider text-[#52525b] mb-1 flex items-center justify-between">
                  <span>Book Content (up to 300 words)</span>
                  <span className={`text-[10px] font-normal ${((form.content || "").trim().split(/\s+/).filter(Boolean).length) > 300 ? 'text-[#dc2626]' : 'text-[#71717a]'}`} data-testid="form-content-wordcount">
                    {(form.content || "").trim() ? (form.content || "").trim().split(/\s+/).filter(Boolean).length : 0} / 300 words
                  </span>
                </div>
                <textarea
                  value={form.content || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    const words = val.trim() ? val.trim().split(/\s+/).filter(Boolean) : [];
                    if (words.length <= 300) setForm({ ...form, content: val });
                    else {
                      // Trim to 300 words
                      setForm({ ...form, content: words.slice(0, 300).join(" ") });
                      toast.warning("Content capped at 300 words");
                    }
                  }}
                  rows={10}
                  placeholder="Write detailed book content — chapters overview, key takeaways, binding info, page count, etc."
                  className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm font-body leading-relaxed"
                  data-testid="form-content"
                />
              </label>
              <label className="text-xs md:col-span-2">
                <div className="font-bold uppercase tracking-wider text-[#52525b] mb-1">Description</div>
                <textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="form-description" />
              </label>

              {/* Flags */}
              <div className="md:col-span-2 border-t border-[#e4e4e7] pt-3 mt-1">
                <div className="text-xs font-bold uppercase tracking-wider text-[#ea580c] mb-2">Product Flags</div>
                <div className="flex flex-wrap gap-3">
                  <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_new_arrival} onChange={(e) => setForm({ ...form, is_new_arrival: e.target.checked })} data-testid="form-new-arrival" /> <Sparkles className="w-4 h-4 text-[#3b82f6]" />New Arrival</label>
                  <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} data-testid="form-featured" /> <Star className="w-4 h-4 text-[#a855f7]" />Featured</label>
                  <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.out_of_stock} onChange={(e) => setForm({ ...form, out_of_stock: e.target.checked })} data-testid="form-oos" /> <PackageX className="w-4 h-4 text-[#dc2626]" />Force Out of Stock</label>
                </div>
              </div>

              {/* SEO */}
              <div className="md:col-span-2 border-t border-[#e4e4e7] pt-3 mt-1">
                <div className="text-xs font-bold uppercase tracking-wider text-[#ea580c] mb-2">SEO (leave blank for auto)</div>
              </div>
              <label className="text-xs"><div className="font-bold uppercase tracking-wider text-[#52525b] mb-1">URL Slug</div>
                <input value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated from title" className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="form-slug" />
              </label>
              <label className="text-xs"><div className="font-bold uppercase tracking-wider text-[#52525b] mb-1">Meta Title</div>
                <input value={form.meta_title || ""} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="form-meta-title" />
              </label>
              <label className="text-xs md:col-span-2"><div className="font-bold uppercase tracking-wider text-[#52525b] mb-1">Meta Description</div>
                <textarea value={form.meta_description || ""} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} rows={2} className="w-full border-2 border-[#e4e4e7] px-2 py-1.5 text-sm" data-testid="form-meta-desc" />
              </label>
            </div>
            <div className="p-4 border-t-2 border-[#09090b] flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="border-2 border-[#09090b] px-4 py-2 font-bold text-sm">Cancel</button>
              <button type="submit" className="bg-[#ea580c] text-white font-bold px-4 py-2 text-sm" data-testid="form-submit-btn">{editing ? "Update" : "Create"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
